import {
  FrontendAddonHeaderRawForm,
  FrontendAddonRaw,
  FrontendAddonRawForm,
  FrontendAddonTargetVersionRaw,
  FrontendAddonVersionRaw,
  FrontendAddonWithTargetVersionRaw,
} from "@/domain/FrontendAddon";
import { API_CLIENT } from "../config";
import { getAPIConfigs } from "../helpers";

const apiUrlFrontendAddons = "admin/frontend_addons";

export const getAdminFrontendAddons = async ({
  organizationId,
}: {
  organizationId?: string;
}): Promise<FrontendAddonWithTargetVersionRaw[]> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const response = await API_CLIENT.get<{
    items: FrontendAddonWithTargetVersionRaw[];
  }>(`${apiUrlFrontendAddons}`, {
    ...apiOrganizationIdParams,
  });
  return response.data.items;
};

export const getAdminFrontendAddon = async ({
  organizationId,
  frontendAddonId,
}: {
  organizationId?: string;
  frontendAddonId: FrontendAddonRaw["id"];
}): Promise<FrontendAddonWithTargetVersionRaw> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const response = await API_CLIENT.get<{
    item: FrontendAddonWithTargetVersionRaw;
  }>(`${apiUrlFrontendAddons}/${frontendAddonId}`, {
    ...apiOrganizationIdParams,
  });
  return response.data.item;
};

export const postAdminCreateFrontendAddon = async ({
  organizationId,
  FrontendAddonRawForm,
  file,
}: {
  organizationId?: string;
  FrontendAddonRawForm: FrontendAddonRawForm;
  file: File;
}): Promise<FrontendAddonRaw> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const form = new FormData();
  form.append("file", file);
  if (FrontendAddonRawForm.description) {
    form.append("description", FrontendAddonRawForm.description);
  }
  form.append("privacy_setting", FrontendAddonRawForm.privacy_setting);
  const response = await API_CLIENT.post<{ item: FrontendAddonRaw }>(
    `${apiUrlFrontendAddons}`,
    form,
    {
      ...apiOrganizationIdParams,
    }
  );
  return response.data.item;
};

export const postAdminEditFrontendAddonHeader = async ({
  FrontendAddonHeaderRawForm,
  frontendAddonId,
}: {
  FrontendAddonHeaderRawForm: FrontendAddonHeaderRawForm;
  frontendAddonId: FrontendAddonRaw["id"];
}): Promise<FrontendAddonRaw> => {
  const response = await API_CLIENT.post<{ item: FrontendAddonRaw }>(
    `${apiUrlFrontendAddons}/${frontendAddonId}`,
    FrontendAddonHeaderRawForm
  );
  return response.data.item;
};

export const postAdminDisableFrontendAddon = async ({
  frontendAddonId,
}: {
  frontendAddonId: FrontendAddonRaw["id"];
}): Promise<void> => {
  const response = await API_CLIENT.post<{ item: FrontendAddonRaw }>(
    `${apiUrlFrontendAddons}/${frontendAddonId}/delete`
  );
};

export const postAdminEnableFrontendAddon = async ({
  frontendAddonId,
}: {
  frontendAddonId: FrontendAddonRaw["id"];
}): Promise<void> => {
  const response = await API_CLIENT.post<{ item: FrontendAddonRaw }>(
    `${apiUrlFrontendAddons}/${frontendAddonId}/enable`,
    undefined
  );
};

export const postAdminRemoveFrontendAddon = async ({
  frontendAddonId,
}: {
  frontendAddonId: FrontendAddonRaw["id"];
}): Promise<void> => {
  await API_CLIENT.post(`${apiUrlFrontendAddons}/${frontendAddonId}/remove`);
};

export const getAdminFrontendAddonVersions = async ({
  frontendAddonId,
}: {
  frontendAddonId: FrontendAddonRaw["id"];
}): Promise<{
  items: FrontendAddonVersionRaw[];
  // latest 指定でこのアドオンを利用している組織 (組織が無いときは null で返る)
  latest_usage_orgs: { org_id: string; name: string }[] | null;
}> => {
  const response = await API_CLIENT.get<{
    items: FrontendAddonVersionRaw[];
    latest_usage_orgs: { org_id: string; name: string }[] | null;
  }>(`${apiUrlFrontendAddons}/${frontendAddonId}/frontend_versions`);
  return response.data;
};

export const postAdminCreateFrontendAddonVersion = async ({
  frontendAddonId,
  file,
}: {
  frontendAddonId: FrontendAddonRaw["id"];
  file: File;
}): Promise<FrontendAddonVersionRaw> => {
  const form = new FormData();
  form.append("file", file);
  const response = await API_CLIENT.post<{ item: FrontendAddonVersionRaw }>(
    `${apiUrlFrontendAddons}/${frontendAddonId}/frontend_versions`,
    form
  );
  return response.data.item;
};

export const postAdminEditFrontendAddonVersion = async ({
  frontendAddonId,
  FrontendAddonVersionId,
  file,
}: {
  frontendAddonId: FrontendAddonRaw["id"];
  FrontendAddonVersionId: FrontendAddonVersionRaw["id"];
  file: File;
}): Promise<FrontendAddonVersionRaw> => {
  const form = new FormData();
  form.append("file", file);
  const response = await API_CLIENT.post<{ item: FrontendAddonVersionRaw }>(
    `${apiUrlFrontendAddons}/${frontendAddonId}/frontend_versions/${FrontendAddonVersionId}`,
    form
  );
  return response.data.item;
};

export const postAdminRemoveFrontendAddonVersion = async ({
  frontendAddonId,
  FrontendAddonVersionId,
}: {
  frontendAddonId: FrontendAddonRaw["id"];
  FrontendAddonVersionId: FrontendAddonVersionRaw["id"];
}): Promise<void> => {
  await API_CLIENT.post(
    `${apiUrlFrontendAddons}/${frontendAddonId}/frontend_versions/${FrontendAddonVersionId}/remove`,
    undefined
  );
};

export const postAdminSetFrontendAddonVersion = async ({
  organizationId,
  frontendAddonId,
  targetVersion,
}: {
  organizationId: string;
  frontendAddonId: FrontendAddonRaw["id"];
  targetVersion: FrontendAddonTargetVersionRaw;
}): Promise<void> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  await API_CLIENT.post(
    `${apiUrlFrontendAddons}/${frontendAddonId}/frontend_masters`,
    targetVersion,
    {
      ...apiOrganizationIdParams,
    }
  );
};
